import { css } from '@styled-system/css';
import { Checkbox, InputNumber } from 'antd';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';

import { FilterValues } from '@/features/analysisPageNiches/types';

interface NicheFilterModalProps {
  isOpen: boolean;
  filters: FilterValues;
  onClose: () => void;
  onFilterChange: (filters: FilterValues) => void;
  buttonRef: HTMLDivElement | null;
}

export const NicheFilterModal: FC<NicheFilterModalProps> = ({
  isOpen,
  filters,
  onClose,
  onFilterChange,
  buttonRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        buttonRef &&
        !buttonRef.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, buttonRef, onClose]);

  if (!isOpen) return null;

  const handleNicheTypeChange = (type: 'brand' | 'category', checked: boolean) => {
    const currentTypes = filters.nicheTypes || [];
    const newTypes = checked ? [...currentTypes, type] : currentTypes.filter((t) => t !== type);
    onFilterChange({ ...filters, nicheTypes: newTypes.length > 0 ? newTypes : undefined });
  };

  return (
    <div
      ref={modalRef}
      className={css({
        position: 'absolute',
        top: '-20px',
        left: 'calc(100% + 3px)',
        bg: 'base.segBg',
        border: '0.5px solid',
        borderColor: 'base.border',
        borderRadius: 'lg',
        py: '15px',
        w: '495px',
        zIndex: 1000,
      })}
    >
      {/* Title */}
      <div className={css({ mb: 'md', px: '12px' })}>
        <h3
          className={css({
            fontSize: '13px',
            fontWeight: '500',
            fontFamily: 'Manrope, sans-serif',
            color: 'text.primary',
            m: 0,
            mb: '2',
          })}
        >
          Ajouter des filtres
        </h3>
        <div className={css({ h: '1px', bg: 'base.border', w: 'full' })} />
      </div>

      <div className={css({ display: 'flex', flexDirection: 'column', gap: 'md', px: '20px' })}>
        {/* Prix moyen de vente */}
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '15px' })}>
          <label
            className={css({
              fontSize: '12px',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: '500',
              color: 'text.secondary',
            })}
          >
            Prix moyen de vente
          </label>
          <div className={css({ display: 'flex', gap: 'lg', alignItems: 'center' })}>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.avgPriceMin}
                onChange={(value) => onFilterChange({ ...filters, avgPriceMin: value ?? undefined })}
                placeholder=""
                formatter={(value) => (value ? `${value} €` : '')}
                parser={(value) => value?.replace(' €', '') as any}
                min={0}
                max={filters.avgPriceMax}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                min.
              </span>
            </div>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.avgPriceMax}
                onChange={(value) => onFilterChange({ ...filters, avgPriceMax: value ?? undefined })}
                placeholder=""
                formatter={(value) => (value ? `${value} €` : '')}
                parser={(value) => value?.replace(' €', '') as any}
                min={filters.avgPriceMin ?? 0}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                max.
              </span>
            </div>
          </div>
        </div>

        {/* Produits analysés */}
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '15px' })}>
          <label
            className={css({
              fontSize: '12px',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: '500',
              color: 'text.secondary',
            })}
          >
            Produits analysés
          </label>
          <div className={css({ display: 'flex', gap: 'lg', alignItems: 'center' })}>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.productCountMin}
                onChange={(value) => onFilterChange({ ...filters, productCountMin: value ?? undefined })}
                placeholder=""
                min={0}
                max={filters.productCountMax}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                min.
              </span>
            </div>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.productCountMax}
                onChange={(value) => onFilterChange({ ...filters, productCountMax: value ?? undefined })}
                placeholder=""
                min={filters.productCountMin ?? 0}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                max.
              </span>
            </div>
          </div>
        </div>

        {/* % de vente */}
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '15px' })}>
          <label
            className={css({
              fontSize: '12px',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: '500',
              color: 'text.secondary',
            })}
          >
            % de vente
          </label>
          <div className={css({ display: 'flex', gap: 'lg', alignItems: 'center' })}>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.soldPctMin}
                onChange={(value) => onFilterChange({ ...filters, soldPctMin: value ?? undefined })}
                placeholder=""
                formatter={(value) => (value ? `${value} %` : '')}
                parser={(value) => value?.replace(' %', '') as any}
                min={0}
                max={filters.soldPctMax ?? 100}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                min.
              </span>
            </div>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.soldPctMax}
                onChange={(value) => onFilterChange({ ...filters, soldPctMax: value ?? undefined })}
                placeholder=""
                formatter={(value) => (value ? `${value} %` : '')}
                parser={(value) => value?.replace(' %', '') as any}
                min={filters.soldPctMin ?? 0}
                max={100}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                max.
              </span>
            </div>
          </div>
        </div>

        {/* Ventes par jour */}
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '15px' })}>
          <label
            className={css({
              fontSize: '12px',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: '500',
              color: 'text.secondary',
            })}
          >
            Ventes par jour
          </label>
          <div className={css({ display: 'flex', gap: 'lg', alignItems: 'center' })}>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.soldPerDayMin}
                onChange={(value) => onFilterChange({ ...filters, soldPerDayMin: value ?? undefined })}
                placeholder=""
                min={0}
                max={filters.soldPerDayMax}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                min.
              </span>
            </div>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.soldPerDayMax}
                onChange={(value) => onFilterChange({ ...filters, soldPerDayMax: value ?? undefined })}
                placeholder=""
                min={filters.soldPerDayMin ?? 0}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                max.
              </span>
            </div>
          </div>
        </div>

        {/* Temps de vente (en heure) */}
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '15px' })}>
          <label
            className={css({
              fontSize: '12px',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: '500',
              color: 'text.secondary',
            })}
          >
            Temps de vente (en heure)
          </label>
          <div className={css({ display: 'flex', gap: 'lg', alignItems: 'center' })}>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.timeToSellMin}
                onChange={(value) => onFilterChange({ ...filters, timeToSellMin: value ?? undefined })}
                placeholder=""
                min={0}
                max={filters.timeToSellMax}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                min.
              </span>
            </div>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.timeToSellMax}
                onChange={(value) => onFilterChange({ ...filters, timeToSellMax: value ?? undefined })}
                placeholder=""
                min={filters.timeToSellMin ?? 0}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                max.
              </span>
            </div>
          </div>
        </div>

        {/* Type de niche */}
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '15px' })}>
          <label
            className={css({
              fontSize: '12px',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: '500',
              color: 'text.secondary',
            })}
          >
            Type de niche
          </label>
          <div className={css({ display: 'flex', gap: 'lg' })}>
            <Checkbox
              checked={filters.nicheTypes?.includes('brand')}
              onChange={(e) => handleNicheTypeChange('brand', e.target.checked)}
              className={css({ fontSize: '13px', fontFamily: 'Manrope, sans-serif' })}
            >
              Marque
            </Checkbox>
            <Checkbox
              checked={filters.nicheTypes?.includes('category')}
              onChange={(e) => handleNicheTypeChange('category', e.target.checked)}
              className={css({ fontSize: '13px', fontFamily: 'Manrope, sans-serif' })}
            >
              Catégorie
            </Checkbox>
          </div>
        </div>
      </div>
    </div>
  );
};
